import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  Popover,
  Typography,
  TextField,
  Chip,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Stack,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  InputAdornment,
  Drawer,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  LocalShipping as TruckIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  // ConfirmationNumber as TicketIcon, LocalOffer as OfferIcon,
  DateRange as DateRangeIcon,
  LocationOn as LocationIcon,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Phone,
  Add as AddIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { ExpiringLRDialog } from "./ExpiringLRDialog";
import {
  freightList,
  freightComplete,
  freightDelete,
  updateFreight,
  partyList,
} from "../../../Services/Services";
import { pdfGenerator } from "./pdfGenerator";
import { getLorryReceiptBlobUrl } from "./pdfGenerator";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate, useSearchParams } from "react-router-dom";
import { T } from "../../../theme";

const TABLE_COLUMN_COUNT = 28;

// ── Shared Styles ──────────────────────────────────────────────────
const sq = { borderRadius: "4px !important" };

const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.white,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};

const dangerBtn = {
  ...sq,
  border: `1.5px solid ${T.redBorder}`,
  bgcolor: T.redBg,
  color: T.red,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#fee2e2", borderColor: T.red, boxShadow: "none" },
};

const primaryBtn = {
  ...sq,
  bgcolor: T.primary,
  color: T.white,
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryDark,
    boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
  },
};

const successBtn = {
  ...sq,
  border: `1.5px solid ${T.successBorder}`,
  bgcolor: T.successBg,
  color: T.success,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#d1fae5", borderColor: T.success, boxShadow: "none" },
};

const iconBtn = { ...ghostBtn, minWidth: 0, px: 1 };

const inputSx = {
  "& .MuiOutlinedInput-root": {
    ...sq,
    bgcolor: T.white,
    "& fieldset": { border: `1.5px solid ${T.border}` },
    "&:hover fieldset": { border: `1.5px solid ${T.borderHover}` },
    "&.Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
    "&.Mui-focused": { boxShadow: `0 0 0 3px ${T.primaryLight}` },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
};

const disabledFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    ...sq,
    bgcolor: "#f3f4f6",
    "& fieldset": { border: `1.5px solid ${T.border}` },
    "&.Mui-disabled fieldset": { border: `1.5px solid ${T.border}` },
  },
};

const filterInputSx = {
  "& .MuiInputBase-root": { height: 34, ...sq, bgcolor: T.white },
  "& fieldset": { border: `1.5px solid ${T.border}` },
  "& :hover fieldset": { border: `1.5px solid ${T.borderHover}` },
  "& .Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
};

// ── Helpers ────────────────────────────────────────────────────────
const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const formatCurrency = (a) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(a || 0);

const getInitials = (s) =>
  s
    ?.split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) ?? "";

const getPrimaryPackage = (row) => row?.package_items?.[0] || {};

const getRefNo = (row) => {
  if (!row?.billing) return "—";
  const p =
    row.billing.branch_name === "RAIPUR"
      ? "SRE"
      : row.billing.branch_name === "BILASPUR"
        ? "JHT"
        : row.billing.branch_name
          ? "MBR"
          : "GEN";
  return `${p}/${row.billing.billing_id}/${getInitials(row.billing.party_name)}`;
};

// E-way bill expiry check karega
const getEwayExpiryStatus = (validityDate) => {
  if (!validityDate) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const expiry = new Date(validityDate);
  expiry.setHours(0, 0, 0, 0);

  const diffDays = Math.floor((expiry - today) / (1000 * 60 * 60 * 24));

  if (diffDays < 0)
    return {
      status: "expired",
      label: "Expired",
      color: "#dc2626",
      bg: "#fee2e2",
    };
  if (diffDays === 0)
    return {
      status: "today",
      label: "Expires Today",
      color: "#dc2626",
      bg: "#fee2e2",
    };
  if (diffDays === 1)
    return {
      status: "tomorrow",
      label: "Expires Tomorrow",
      color: "#d97706",
      bg: "#fef3c7",
    };
  if (diffDays === 2)
    return {
      status: "soon",
      label: "Expires in 2 days",
      color: "#d97706",
      bg: "#fef3c7",
    };
  return null;
};

// ── Dialog Header ──────────────────────────────────────────────────
const DialogHeader = ({ title, onClose }) => (
  <Box
    sx={{
      background: "#6862e0",
      px: 2.5,
      py: 1.75,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <Typography
      sx={{
        fontWeight: 700,
        color: T.white,
        fontSize: "0.88rem",
        letterSpacing: "0.01em",
      }}
    >
      {title}
    </Typography>
    <IconButton
      onClick={onClose}
      size="small"
      sx={{
        color: "rgba(255,255,255,0.8)",
        p: 0.5,
        ...sq,
        "&:hover": { bgcolor: "rgba(255,255,255,0.15)", color: T.white },
      }}
    >
      <CloseIcon sx={{ fontSize: 17 }} />
    </IconButton>
  </Box>
);

// ── ActionButton ───────────────────────────────────────────────────
const ActionButton = ({
  tip,
  icon,
  isDelete,
  isSuccess,
  isEdit,
  isView,
  isPrint,
  color,
  onClick,
  disabled,
}) => {
  const c = isDelete
    ? "#dc2626"
    : isEdit
      ? "#757575"
      : isView
        ? "#1976d2"
        : isSuccess || isPrint
          ? T.success
          : color || T.primary;

  const bg = isDelete
    ? T.redBg
    : isEdit
      ? "#f5f5f5"
      : isView
        ? "#e3f2fd"
        : isSuccess || isPrint
          ? T.successBg
          : T.primaryBg;

  const bd = isDelete
    ? T.redBorder
    : isEdit
      ? "#e0e0e0"
      : isView
        ? "#bbdefb"
        : isSuccess || isPrint
          ? T.successBorder
          : T.border;

  const hBg = isDelete
    ? "#fee2e2"
    : isEdit
      ? "#eeeeee"
      : isView
        ? "#bbdefb"
        : isSuccess || isPrint
          ? "#d1fae5"
          : T.primaryLight;

  return (
    <Tooltip title={tip} arrow placement="top">
      <span>
        <IconButton
          size="small"
          disabled={disabled}
          onClick={onClick}
          sx={{
            width: 20,
            height: 20,
            ...sq,
            color: c,
            bgcolor: bg,
            border: `1px solid ${bd}`,
            transition: "all 0.18s",
            "&:hover": {
              bgcolor: hBg,
              borderColor: c,
              transform: "translateY(-1px)",
            },
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 12 } })}
        </IconButton>
      </span>
    </Tooltip>
  );
};

// ── TruncatedCell ──────────────────────────────────────────────────
const TruncatedCell = ({ text, maxWidth = 140 }) => (
  <Tooltip title={text || "—"} arrow>
    <Typography
      sx={{
        fontSize: "0.72rem",
        color: T.text,
        maxWidth,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontWeight: 500,
      }}
    >
      {text || "—"}
    </Typography>
  </Tooltip>
);

// ── InfoRow ────────────────────────────────────────────────────────
const InfoRow = ({ Icon, label, val }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
    <Box
      sx={{
        width: 22,
        height: 22,
        ...sq,
        bgcolor: T.white,
        border: `1px solid ${T.border}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: 12, color: T.primary }} />
    </Box>
    <Box>
      <Typography
        sx={{
          fontSize: "0.6rem",
          color: T.textMuted,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.72rem",
          color: T.text,
          fontWeight: 500,
          lineHeight: 1.3,
        }}
      >
        {val || "—"}
      </Typography>
    </Box>
  </Box>
);

// ── DeletePopover ──────────────────────────────────────────────────
const DeletePopover = ({ anchorEl, onClose, onConfirm }) => (
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    transformOrigin={{ vertical: "top", horizontal: "center" }}
    PaperProps={{
      sx: {
        ...sq,
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        border: `1.5px solid ${T.border}`,
        bgcolor: T.white,
      },
    }}
  >
    <Box sx={{ p: 2, maxWidth: 240 }}>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            ...sq,
            bgcolor: T.redBg,
            border: `1.5px solid ${T.redBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DeleteIcon sx={{ fontSize: 20, color: T.red }} />
        </Box>
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.78rem",
          color: T.text,
          textAlign: "center",
          mb: 0.5,
        }}
      >
        Delete LR Record?
      </Typography>
      <Typography
        sx={{
          fontSize: "0.67rem",
          color: T.textSub,
          fontWeight: 500,
          textAlign: "center",
          mb: 1.75,
        }}
      >
        This action cannot be undone.
      </Typography>
      <Box sx={{ display: "flex", gap: 0.75 }}>
        <Button
          fullWidth
          size="small"
          onClick={onClose}
          sx={{ ...ghostBtn, height: 30, fontSize: "0.7rem" }}
        >
          Cancel
        </Button>
        <Button
          fullWidth
          size="small"
          onClick={onConfirm}
          sx={{ ...dangerBtn, height: 30, fontSize: "0.7rem" }}
        >
          Delete
        </Button>
      </Box>
    </Box>
  </Popover>
);

// ── SettlePopover ──────────────────────────────────────────────────
const SettlePopover = ({ anchorEl, onClose, onConfirm }) => (
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    transformOrigin={{ vertical: "top", horizontal: "center" }}
    PaperProps={{
      sx: {
        ...sq,
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        border: `1.5px solid ${T.border}`,
        bgcolor: T.white,
      },
    }}
  >
    <Box sx={{ p: 2, maxWidth: 240 }}>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            ...sq,
            bgcolor: T.successBg,
            border: `1.5px solid ${T.successBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CheckCircle sx={{ fontSize: 20, color: T.success }} />
        </Box>
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.78rem",
          color: T.text,
          textAlign: "center",
          mb: 0.5,
        }}
      >
        Settle LR?
      </Typography>
      <Typography
        sx={{
          fontSize: "0.67rem",
          color: T.textSub,
          fontWeight: 500,
          textAlign: "center",
          mb: 1.75,
        }}
      >
        Mark this record as completed?
      </Typography>
      <Box sx={{ display: "flex", gap: 0.75 }}>
        <Button
          fullWidth
          size="small"
          onClick={onClose}
          sx={{ ...ghostBtn, height: 30, fontSize: "0.7rem" }}
        >
          Cancel
        </Button>
        <Button
          fullWidth
          size="small"
          onClick={onConfirm}
          sx={{ ...successBtn, height: 30, fontSize: "0.7rem" }}
        >
          Settle
        </Button>
      </Box>
    </Box>
  </Popover>
);

// ── MobileFreightCard ──────────────────────────────────────────────
const MobileFreightCard = ({
  row,
  idx,
  expanded,
  onToggle,
  onEdit,
  onDelete,
  onPrint,
  onSettle,
  onPreview,
}) => {
  const isCompleted = row.is_completed;
  const pkg = getPrimaryPackage(row);

  const SectionTitle = ({ emoji, title, color = T.primary }) => (
    <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color, mb: 0.5 }}>
      {emoji} {title}
    </Typography>
  );

  const DataPair = ({ label, value, color, xs = 6 }) => (
    <Grid item xs={xs}>
      <Typography
        sx={{
          fontSize: "0.58rem",
          color: T.textMuted,
          fontWeight: 600,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: "0.7rem", fontWeight: 600, color: color || T.text }}
      >
        {value ?? "—"}
      </Typography>
    </Grid>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        ...sq,
        border: `1.5px solid ${expanded ? T.primary : T.border}`,
        bgcolor: T.white,
        overflow: "hidden",
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: expanded ? "0 2px 12px rgba(79,70,229,0.1)" : "none",
      }}
    >
      {/* Top Row */}
      <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 64 }}>
        <Box
          sx={{
            width: 60,
            flexShrink: 0,
            bgcolor: expanded ? T.primary : T.primaryBg,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRight: `1.5px solid ${expanded ? T.primary : T.border}`,
            transition: "background-color 0.2s",
            gap: 0.25,
          }}
        >
          <TruckIcon
            sx={{ fontSize: 22, color: expanded ? T.white : T.primary }}
          />
          <Typography
            sx={{
              fontSize: "0.52rem",
              fontWeight: 700,
              color: expanded ? T.white : T.primary,
            }}
          >
            #{idx + 1}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: 1.5,
            py: 1,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.82rem",
              color: T.primary,
              lineHeight: 1.2,
              mb: 0.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              textAlign:"left"
            }}
          >
            {row.lr_number || "—"}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.65rem",
              color: T.textSub,
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              mb: 0.25,
              textAlign:"left"
            }}
          >
            {row.party_name || "—"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <LocationIcon sx={{ fontSize: 10, color: T.textMuted }} />
            <Typography
              sx={{
                fontSize: "0.62rem",
                color: T.textMuted,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textAlign:"left"
              }}
            >
              {row.from_location || "—"} → {row.to_location || "—"}
            </Typography>
          </Box>
        </Box>

        {/* Right section — Net Payable removed, Truck No shown instead */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "flex-end",
            px: 1.2,
            py: 1,
            minWidth: 100,
          }}
        >
          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{ fontSize: "0.55rem", color: T.textMuted, fontWeight: 600 }}
            >
              Truck No
            </Typography>
            <Typography
              sx={{ fontWeight: 800, fontSize: "0.75rem", color: T.text }}
            >
              {row.truck_no || "—"}
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={onToggle}
            startIcon={
              expanded ? (
                <VisibilityOff sx={{ fontSize: 12 }} />
              ) : (
                <Visibility sx={{ fontSize: 12 }} />
              )
            }
            sx={{
              ...ghostBtn,
              height: 26,
              fontSize: "0.62rem",
              px: 1,
              "& .MuiButton-startIcon": { mr: "3px", ml: 0 },
            }}
          >
            {expanded ? "Hide" : "Details"}
          </Button>
        </Box>
      </Box>

      {/* Expanded */}
      {expanded && (
        <Box
          sx={{
            bgcolor: T.white,
            borderTop: `1.5px solid ${T.border}`,
            p: 1.5,
          }}
        >
          <Stack spacing={1.25}>
            {/* Basic Info */}
            <Box>
              <SectionTitle emoji="📋" title="Basic Info" />
              <Grid container spacing={0.5}>
                <DataPair label="LR" value={row.lr_number} />
                <DataPair label="ID" value={row.freight_id} />
                <DataPair label="E-way Bill" value={row.eway_bill_no} />
                <DataPair label="Invoice" value={row.invoice_no} />
                <DataPair
                  label="Loading"
                  value={formatDate(row.loading_date)}
                />
                <DataPair
                  label="Unloading"
                  value={formatDate(row.unloading_date)}
                />
              </Grid>
            </Box>

            {/* Package */}
            <Box
              sx={{
                p: 1,
                bgcolor: T.white,
                border: `1px solid ${T.border}`,
                ...sq,
              }}
            >
              <SectionTitle emoji="📦" title="Package" />
              <Grid container spacing={0.5}>
                <DataPair label="Packages" value={pkg.packages} />
                <DataPair label="Rate" value={`₹${pkg.rate || 0}`} />
                <DataPair
                  label="Actual Wt"
                  value={pkg.actual_weight || 0}
                  xs={4}
                />
                <DataPair
                  label="Charged Wt"
                  value={pkg.charged_weight || 0}
                  xs={4}
                />
                <DataPair label="Amount" value={`₹${pkg.amount || 0}`} xs={4} />
                {pkg.description && (
                  <DataPair
                    label="Description"
                    value={pkg.description}
                    xs={12}
                  />
                )}
              </Grid>
            </Box>

            {/* Weight */}
            <Box
              sx={{
                p: 1,
                bgcolor: T.white,
                border: `1px solid ${T.border}`,
                ...sq,
              }}
            >
              <SectionTitle emoji="⚖️" title="Weight" />
              <Grid container spacing={0.5}>
                <DataPair label="Load" value={row.loading_wt || 0} xs={3} />
                <DataPair label="Recv" value={row.received_wt || 0} xs={3} />
                <DataPair
                  label="Short"
                  value={row.shortage_wt || 0}
                  color={T.red}
                  xs={3}
                />
                <DataPair label="Bill" value={row.billing_wt || 0} xs={3} />
              </Grid>
            </Box>

            {/* Broker Info — NEW */}
            {(row.broker_party_name || row.broker_total_amt > 0) && (
              <Box
                sx={{
                  p: 1,
                  bgcolor: "#f0fdf4",
                  border: `1px solid #bbf7d0`,
                  ...sq,
                }}
              >
                <SectionTitle
                  emoji="🤝"
                  title="Broker Details"
                  color="#15803d"
                />
                <Grid container spacing={0.5}>
                  <DataPair
                    label="Broker"
                    value={row.broker_party_name || "—"}
                    xs={12}
                  />
                  <DataPair
                    label="Total Amt"
                    value={`₹${row.broker_total_amt || 0}`}
                    xs={4}
                  />
                  <DataPair
                    label="Commission"
                    value={`₹${row.broker_commission_amt || 0}`}
                    xs={4}
                  />
                  <DataPair
                    label="Net Amt"
                    value={`₹${row.broker_net_amt || 0}`}
                    color={T.primary}
                    xs={4}
                  />
                </Grid>
              </Box>
            )}

            {/* Charges */}
            <Box>
              <SectionTitle emoji="💰" title="Charges" />
              <Grid container spacing={0.5}>
                {[
                  ["Rate", `₹${row.freight_rate || 0}`],
                  ["LR", `₹${row.freight_amt || 0}`],
                  ["TDS", `₹${row.tds_amt || 0} (${row.tds_per || 0}%)`],
                  ["Shortage", `₹${row.shortage_amt || 0}`],
                  ["Commission", `₹${row.commission || 0}`],
                  ["Other", `₹${row.other_exp || 0}`],
                  [
                    "Demurrage",
                    `${row.demurrage_days || 0}d @ ₹${row.demurrage_rate || 0}`,
                  ],
                ].map(([l, v]) => (
                  <DataPair key={l} label={l} value={v} />
                ))}
              </Grid>
            </Box>

            {/* Advance */}
            <Box
              sx={{
                p: 1,
                bgcolor: "#fffbeb",
                border: `1px solid #fde68a`,
                ...sq,
              }}
            >
              <SectionTitle emoji="💵" title="Advance" color="#d97706" />
              <Grid container spacing={0.5}>
                <DataPair
                  label="Mines"
                  value={`₹${row.ad_mines_exp || 0}`}
                  xs={4}
                />
                <DataPair
                  label="Diesel"
                  value={`₹${row.ad_diesel_exp || 0}`}
                  xs={4}
                />
                <DataPair
                  label="Cash"
                  value={`₹${row.ad_cash_exp || 0}`}
                  xs={4}
                />
                <DataPair
                  label="Total Advance"
                  value={`₹${row.total_advance || 0}`}
                  color="#d97706"
                  xs={12}
                />
              </Grid>
            </Box>

            {/* Owner */}
            {(row.truck_owner || row.phone_no || row.pan_no) && (
              <Box>
                <SectionTitle emoji="👤" title="Owner Details" />
                <Stack spacing={0.5}>
                  {row.truck_owner && (
                    <InfoRow
                      Icon={TruckIcon}
                      label="Owner"
                      val={row.truck_owner}
                    />
                  )}
                  {row.phone_no && (
                    <InfoRow Icon={Phone} label="Phone" val={row.phone_no} />
                  )}
                  {row.pan_no && (
                    <InfoRow Icon={BusinessIcon} label="PAN" val={row.pan_no} />
                  )}
                </Stack>
              </Box>
            )}

            {/* Summary */}
            <Box
              sx={{
                p: 1,
                bgcolor: T.primaryLight,
                border: `1.5px solid ${T.border}`,
                ...sq,
              }}
            >
              <Grid container spacing={0.5}>
                <DataPair
                  label="Net Amount"
                  value={`₹${row.net_amount || 0}`}
                />
                <DataPair
                  label="Net Payable"
                  value={`₹${row.net_payable || 0}`}
                  color={T.primary}
                />
                <DataPair
                  label="Paid"
                  value={`₹${(row.paid_amount || 0).toFixed(1)}`}
                  color={T.success}
                />
                <DataPair
                  label="Balance"
                  value={`₹${(row.bal_amount || 0).toFixed(1)}`}
                  color={T.red}
                />
                <DataPair label="Ref No" value={getRefNo(row)} xs={12} />
              </Grid>
            </Box>

            {row.remark && (
              <InfoRow Icon={EditIcon} label="Remark" val={row.remark} />
            )}
            {row.additional_info && (
              <InfoRow
                Icon={EditIcon}
                label="Additional Info"
                val={row.additional_info}
              />
            )}
          </Stack>

          {/* Actions */}
          <Box
            sx={{
              display: "flex",
              gap: 0.75,
              mt: 1.5,
              pt: 1.25,
              borderTop: `1px dashed ${T.border}`,
              justifyContent: "flex-end",
              flexWrap: "wrap",
            }}
          >
            {!isCompleted && (
              <Button
                size="small"
                startIcon={<CheckCircle sx={{ fontSize: 13 }} />}
                onClick={(e) => onSettle(e, row.freight_id)}
                sx={{ ...successBtn, height: 30, px: 1.5 }}
              >
                Settle
              </Button>
            )}
            <Button
              size="small"
              startIcon={<Visibility sx={{ fontSize: 13 }} />}
              onClick={() => onPreview(row)}
              sx={{ ...ghostBtn, height: 30, px: 1.5 }}
            >
              Preview
            </Button>
            <Button
              size="small"
              startIcon={<PrintIcon sx={{ fontSize: 13 }} />}
              onClick={() => onPrint(row)}
              sx={{ ...ghostBtn, height: 30, px: 1.5 }}
            >
              Print
            </Button>
            <Button
              size="small"
              startIcon={<EditIcon sx={{ fontSize: 13 }} />}
              onClick={() => onEdit(row)}
              sx={{ ...ghostBtn, height: 30, px: 1.5 }}
            >
              Edit
            </Button>
            <Button
              size="small"
              startIcon={<DeleteIcon sx={{ fontSize: 13 }} />}
              onClick={(e) => onDelete(e, row.freight_id)}
              sx={{ ...dangerBtn, height: 30, px: 1.5 }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

// ── Main Component ─────────────────────────────────────────────────
export default function FreightList() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const [data, setData] = useState([]);
  const [partyFilter, setPartyFilter] = useState("");
  const [tpFilter, setTpFilter] = useState("");
  const [loadingDateFilter, setLoadingDateFilter] = useState(null);
  const [fromFilter, setFromFilter] = useState("");
  const [toBillingFilter, setToBillingFilter] = useState("");
  const [truckFilter, setTruckFilter] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState({});
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [settleAnchorEl, setSettleAnchorEl] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [settleId, setSettleId] = useState(null);
  const [partyOptions, setParties] = useState([]);
  const [excelModalOpen, setExcelModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [expiringOpen, setExpiringOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const dateFilter = searchParams.get("date_filter");

  const handleCardToggle = (id) =>
    setExpandedCardId((p) => (p === id ? null : id));

  const pageRef = useRef(1);
  const loadingRef = useRef(false);

  useEffect(() => {
    const fetchParties = async () => {
      try {
        const res = await partyList({ party_type: "consignor" });
        setParties(Array.isArray(res) ? res : []);
      } catch (error) {
        console.error("Failed to fetch parties:", error);
        setParties([]);
      }
    };
    fetchParties();
  }, []);

  const handlePreviewOpen = async (row) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const url = await getLorryReceiptBlobUrl(row);
      setPreviewUrl(url);
    } catch (err) {
      console.error("Error generating preview:", err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreviewClose = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl); // avoid memory leaks
    setPreviewUrl(null);
    setPreviewOpen(false);
  };

  const fetchMoreParties = useCallback(
    async (reset = false, filters = {}) => {
      if (loadingRef.current && !reset) return;
      loadingRef.current = true;
      setLoading(true);
      try {
        const currentPage = reset ? 1 : pageRef.current;
        const dataRes = await freightList(currentPage, {
          party_name: filters.party_name ?? partyFilter,
          do_no: filters.do_no ?? tpFilter,
          ref_no: filters.ref_no ?? toBillingFilter,
          truck_no: filters.truck_no ?? truckFilter,
          loading_date: filters.loading_date ?? loadingDateFilter,
          from_to: filters.from_to ?? fromFilter,
          date_filter: filters.date_filter ?? dateFilter,
        });
        if (dataRes?.results?.length) {
          setData((prev) =>
            reset ? dataRes.results : [...prev, ...dataRes.results],
          );
          pageRef.current = reset ? 2 : pageRef.current + 1;
          setHasMore(!!dataRes.next);
        } else {
          if (reset) setData([]);
          setHasMore(false);
        }
      } catch (err) {
        console.error("Error fetching freight list:", err);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    },
    [
      partyFilter,
      tpFilter,
      toBillingFilter,
      truckFilter,
      loadingDateFilter,
      fromFilter,
      dateFilter,
    ],
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      pageRef.current = 1;
      fetchMoreParties(true, {
        party_name: partyFilter,
        do_no: tpFilter,
        ref_no: toBillingFilter,
        truck_no: truckFilter,
        loading_date: loadingDateFilter,
        from_to: fromFilter,
        date_filter: dateFilter, // NEW
      });
      setExpandedCardId(null);
    }, 500);
    return () => clearTimeout(handler);
  }, [
    partyFilter,
    tpFilter,
    toBillingFilter,
    truckFilter,
    loadingDateFilter,
    fromFilter,
    dateFilter, // NEW
    fetchMoreParties,
  ]);

  const handleComplete = async (id) => {
    const d = await freightComplete(id);
    setData((p) => p.filter((r) => r.freight_id !== d.freight_id));
    setSettleAnchorEl(null);
    setSettleId(null);
  };

  const handleDelete = async (id) => {
    const d = await freightDelete(id);
    setData((p) => p.filter((r) => r.freight_id !== d.freight_id));
    setDeleteAnchorEl(null);
    setDeleteId(null);
  };

  const handleEditOpen = (row) => {
    navigate(`/dashboard/freight-form/edit/${row.freight_id}`, {
      state: { editData: row },
    });
  };

  const handleEditChange = (field, value) => {
    const u = { ...editRow, [field]: value };
    const g = (f, v) => parseFloat(f === field ? v : u[f]) || 0;
    const loadW = g("loading_wt", value),
      recvW = g("received_wt", value);
    const fRate = g("freight_rate", value),
      tdsR = g("tds_per", value),
      sRate = g("shortage_rate", value);
    const comm = g("commission", value),
      mines = g("ad_mines_exp", value);
    const diesel = g("ad_diesel_exp", value),
      cash = g("ad_cash_exp", value),
      other = g("other_exp", value);
    u.billing_wt = Math.min(loadW, recvW).toFixed(2);
    const shortage = (loadW - recvW) * 1000;
    u.shortage_wt = shortage > 0 ? shortage.toFixed(2) : "0";
    u.freight_amt = (fRate * parseFloat(u.billing_wt)).toFixed(2);
    u.tds_amt = ((parseFloat(u.freight_amt) * tdsR) / 100).toFixed(2);
    u.shortage_amt = (parseFloat(u.shortage_wt) * sRate).toFixed(2);
    u.total_advance = (mines + diesel + cash).toFixed(2);
    u.net_amount = (
      parseFloat(u.freight_amt) -
      parseFloat(u.tds_amt) -
      parseFloat(u.shortage_amt) -
      parseFloat(u.total_advance) -
      comm
    ).toFixed(2);
    u.net_payable = (parseFloat(u.net_amount) - other).toFixed(2);
    setEditRow(u);
  };

  const handleEditSave = async () => {
    const s = { ...editRow, receive_amount: editRow.receive_amount || 0 };
    s.bal_amount = (
      parseFloat(s.net_payable || 0) - parseFloat(s.paid_amount || 0)
    ).toFixed(2);
    const res = await updateFreight(s.freight_id, s);
    setData((p) => p.map((r) => (r.freight_id === res.freight_id ? res : r)));
    setEditDialogOpen(false);
  };

  const handlePrintSingleRow = (row) => pdfGenerator(row);

  const handleExportSelectedToExcel = async () => {
    const XLSX = await import("xlsx-js-style");

    const sel = data.filter((r) => selectedRows.includes(r.freight_id));

    const headers = [
      "Sr No",
      "Party Name",
      "Broker Name",
      "LR No",
      "Loading Date",
      "Loading Out Date",
      "Unloading Date",
      "Unloading Out Date",
      "Truck No.",
      "From",
      "To",
      "Package",
      "Package Description",
      "Actual Weight",
      "Charged Weight",
      "Package Rate",
      "Package Amount",
      "Demurrage Days",
      "Demurrage Rate",
      "E-way Bill No",
      "E-way Bill Date",
      "Invoice No",
      "Classification Of Goods",
      "Method Of Packing",
      "Loading Wt",
      "Receiving Wt",
      "Shortage Wt",
      "Billing Wt",
      "LR Rate",
      "LR Amt",
      "TDS %",
      "TDS Amt",
      "Shortage Rate",
      "Shortage Amt",
      "Ad Mines",
      "Ad Diesel",
      "Ad Cash",
      "Total Ad",
      "Commission",
      "Net Amt",
      "Others Exp.",
      "Net Payable",
      "Paid Amt",
      "Bal Amt",
      "Rec Amt",
      "Diff Amt",
      "Payment Remark",
      "Ref. No",
      "Broker Total Amt",
      "Broker Commission Amt",
      "Broker Net Amt",
      "Bill Date",
      "Truck Owner",
      "Phone No.",
      "PAN No.",
      "Remark",
      "Additional Info",
      "Date",
    ];

    const rows = sel.map((row, i) => {
      const pkg = getPrimaryPackage(row);
      return [
        i + 1,
        row.party_name || "—",
        row.broker_party_name || "—",
        row.lr_number || "—",
        row.loading_date || "—",
        row.loading_out_date || "—",
        row.unloading_date || "—",
        row.unloading_out_date || "—",
        row.truck_no || "—",
        row.from_location || "—",
        row.to_location || "—",
        pkg.packages ?? "—",
        pkg.description || "—",
        pkg.actual_weight ?? "—",
        pkg.charged_weight ?? "—",
        pkg.rate ?? "—",
        pkg.amount ?? "—",
        row.demurrage_days ?? "—",
        row.demurrage_rate ?? "—",
        row.eway_bill_no || "—",
        row.eway_bill_date || "—",
        row.invoice_no || "—",
        row.classification_of_goods || "—",
        row.method_of_packing || "—",
        row.loading_wt ?? "—",
        row.received_wt ?? "—",
        row.shortage_wt ?? "—",
        row.billing_wt ?? "—",
        row.freight_rate ?? "—",
        row.freight_amt ?? "—",
        row.tds_per ?? "—",
        row.tds_amt ?? "—",
        row.shortage_rate ?? "—",
        row.shortage_amt ?? "—",
        row.ad_mines_exp ?? "—",
        row.ad_diesel_exp ?? "—",
        row.ad_cash_exp ?? "—",
        row.total_advance ?? "—",
        row.commission ?? "—",
        row.net_amount ?? "—",
        row.other_exp ?? "—",
        row.net_payable ?? "—",
        row.paid_amount ?? "—",
        row.bal_amount ?? "—",
        row.receive_amount ?? "—",
        row.difference_amount ?? "—",
        row.payment_remark || "—",
        getRefNo(row) || "—",
        row.broker_total_amt ?? "—",
        row.broker_commission_amt ?? "—",
        row.broker_net_amt ?? "—",
        row.billing ? formatDate(row.billing.created_at) : "—",
        row.truck_owner || "—",
        row.phone_no || "—",
        row.pan_no || "—",
        row.remark || "—",
        row.additional_info || "—",
        formatDate(row.created_at),
      ];
    });

    // ── Style Definitions ──────────────────────────────────────────
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10, name: "Segoe UI" },
      fill: { fgColor: { rgb: "4F46E5" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: false },
      border: {
        top: { style: "thin", color: { rgb: "3730A3" } },
        bottom: { style: "thin", color: { rgb: "3730A3" } },
        left: { style: "thin", color: { rgb: "3730A3" } },
        right: { style: "thin", color: { rgb: "3730A3" } },
      },
    };

    const cellStyleEven = {
      font: { sz: 10, name: "Segoe UI", color: { rgb: "1E1B4B" } },
      fill: { fgColor: { rgb: "F5F3FF" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: false },
      border: {
        top: { style: "thin", color: { rgb: "E0E7FF" } },
        bottom: { style: "thin", color: { rgb: "E0E7FF" } },
        left: { style: "thin", color: { rgb: "E0E7FF" } },
        right: { style: "thin", color: { rgb: "E0E7FF" } },
      },
    };

    const cellStyleOdd = {
      ...cellStyleEven,
      fill: { fgColor: { rgb: "FFFFFF" } },
    };

    const numberStyle = {
      ...cellStyleEven,
      font: { ...cellStyleEven.font, bold: true, color: { rgb: "4F46E5" } },
    };

    const numberStyleOdd = {
      ...numberStyle,
      fill: { fgColor: { rgb: "FFFFFF" } },
    };

    // ── Build Worksheet Data ───────────────────────────────────────
    const wsData = [
      headers.map((h) => ({ v: h, t: "s", s: headerStyle })),
      ...rows.map((row, rowIdx) =>
        row.map((val, colIdx) => {
          const isEven = rowIdx % 2 === 0;
          const isNumber = colIdx === 0;

          let style;
          if (isNumber) {
            style = isEven ? numberStyle : numberStyleOdd;
          } else {
            style = isEven ? cellStyleEven : cellStyleOdd;
          }

          return {
            v: val ?? "—",
            t: typeof val === "number" ? "n" : "s",
            s: style,
          };
        }),
      ),
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // ── Column Widths (no wrapping, width based on longest content) ──
    const colWidths = headers.map((_, colIdx) => {
      const allValues = [
        headers[colIdx],
        ...rows.map((r) => String(r[colIdx] ?? "")),
      ];
      const maxLen = Math.max(...allValues.map((v) => v.length));
      return { wch: maxLen + 4 };
    });

    ws["!cols"] = colWidths;

    // ── Row Heights ────────────────────────────────────────────────
    ws["!rows"] = [{ hpt: 18 }];

    // ── Freeze Header Row ──────────────────────────────────────────
    ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2" };

    const wb = XLSX.utils.book_new();
    wb.Props = {
      Title: "Freight Report",
      Author: "Staff Management System",
      CreatedDate: new Date(),
    };

    XLSX.utils.book_append_sheet(wb, ws, "LR Report");
    XLSX.writeFile(
      wb,
      `Freight_Report_${new Date().toISOString().split("T")[0]}.xlsx`,
    );

    setExcelModalOpen(false);
    setSelectedRows([]);
  };
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const n = (s) => (s ?? "").toLowerCase().trim();
      const si = n(fromFilter);
      const [fp, tp] = si.split("-").map((s) => s.trim());
      const fm = fp ? n(item.from_location).includes(fp) : true;
      const tm = tp ? n(item.to_location).includes(tp) : true;
      const lm =
        !tp &&
        (n(item.from_location).includes(si) ||
          n(item.to_location).includes(si));
      return (
        (!partyFilter || n(item.party_name).includes(n(partyFilter))) &&
        (!tpFilter || (item.do_no?.toString() || "").includes(tpFilter)) &&
        (!toBillingFilter ||
          (item.billing?.billing_id?.toString() || "").includes(
            toBillingFilter,
          )) &&
        (!truckFilter || n(item.truck_no).includes(n(truckFilter))) &&
        (!loadingDateFilter ||
          item.loading_date ===
            loadingDateFilter.toISOString().split("T")[0]) &&
        ((fp && tp && fm && tm) || (!tp && lm))
      );
    });
  }, [
    data,
    partyFilter,
    tpFilter,
    toBillingFilter,
    truckFilter,
    loadingDateFilter,
    fromFilter,
  ]);

  const expiringCount = useMemo(
    () =>
      data.filter((r) => getEwayExpiryStatus(r.eway_validity_date) !== null)
        .length,
    [data],
  );

  const totals = useMemo(() => {
    const s = (f) => filteredData.reduce((a, c) => a + (+c[f] || 0), 0);
    return {
      freight_amt: s("freight_amt").toFixed(1),
      tds_amt: s("tds_amt").toFixed(1),
      shortage_amt: s("shortage_amt").toFixed(1),
      total_advance: s("total_advance").toFixed(1),
      commission: s("commission").toFixed(1),
      net_amount: s("net_amount").toFixed(1),
      other_exp: s("other_exp").toFixed(1),
      net_payable: s("net_payable").toFixed(1),
      paid_amount: s("paid_amount").toFixed(1),
      bal_amount: s("bal_amount").toFixed(1),
    };
  }, [filteredData]);

  const clearFilters = () => {
    setPartyFilter("");
    setTpFilter("");
    setToBillingFilter("");
    setTruckFilter("");
    setLoadingDateFilter(null);
    setFromFilter("");
  };

  const hasActiveFilters =
    partyFilter ||
    tpFilter ||
    toBillingFilter ||
    truckFilter ||
    loadingDateFilter ||
    fromFilter;

  // ── RENDER ─────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        px: 0,
        pt: 0,
        mt: -3,
        maxWidth: 1400,
        mx: "auto",
        width: "100%",
        height: "calc(100dvh - 80px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {/* ── Toolbar ── */}
      <Box
        sx={{
          mt: 2,
          mb: 1.5,
          flexShrink: 0,
          position: "sticky",
          top: 0,
          zIndex: 50,
          pb: 0.5,
        }}
      >
        <Grid container spacing={0.75} alignItems="center">
          {/* Party */}
          <Grid item xs={12} sm={6} md={2.5}>
            <Autocomplete
              size="small"
              options={partyOptions}
              getOptionLabel={(o) => o?.name || ""}
              value={partyOptions.find((o) => o.name === partyFilter) || null}
              onChange={(_, v) => setPartyFilter(v?.name || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Party…"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon sx={{ fontSize: 14, color: T.primary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={filterInputSx}
                />
              )}
            />
          </Grid>

          {/* Truck */}
          <Grid item xs={6} sm={6} md={2}>
            <TextField
              size="small"
              placeholder="Truck…"
              fullWidth
              value={truckFilter}
              onChange={(e) => setTruckFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocalShippingIcon
                      sx={{ fontSize: 14, color: T.primary }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={filterInputSx}
            />
          </Grid>

          {/* Date */}
          <Grid item xs={6} sm={6} md={2}>
            <TextField
              type="date"
              label="Loading Date"
              size="small"
              fullWidth
              value={
                loadingDateFilter
                  ? loadingDateFilter.toISOString().split("T")[0]
                  : ""
              }
              onChange={(e) =>
                setLoadingDateFilter(
                  e.target.value ? new Date(e.target.value) : null,
                )
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateRangeIcon sx={{ fontSize: 14, color: T.primary }} />
                  </InputAdornment>
                ),
              }}
              sx={filterInputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              size="small"
              placeholder="Route…"
              fullWidth
              value={fromFilter}
              onChange={(e) => setFromFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationIcon sx={{ fontSize: 14, color: T.primary }} />
                  </InputAdornment>
                ),
              }}
              sx={filterInputSx}
            />
          </Grid>

          {/* Action Icons */}
          <Grid item xs={12} sm={6} md="auto">
            <Stack
              direction="row"
              spacing={0.75}
              sx={{ height: 34, width: { xs: "100%", md: "auto" } }}
            >
              {hasActiveFilters && (
                <Tooltip title="Clear Filters" arrow>
                  <Button
                    sx={{
                      ...dangerBtn,
                      minWidth: 34,
                      px: 0,
                      flex: { xs: 1, md: "0 0 auto" },
                    }}
                    onClick={clearFilters}
                  >
                    <ClearIcon sx={{ fontSize: 15 }} />
                  </Button>
                </Tooltip>
              )}
              <Tooltip title="Refresh" arrow>
                <Button
                  sx={{
                    ...iconBtn,
                    minWidth: 34,
                    px: 0,
                    flex: { xs: 1, md: "0 0 auto" },
                  }}
                  onClick={() => {
                    pageRef.current = 1;
                    fetchMoreParties(true);
                  }}
                >
                  <RefreshIcon sx={{ fontSize: 15 }} />
                </Button>
              </Tooltip>
              <Tooltip title="Export Excel" arrow>
                <Button
                  sx={{
                    ...iconBtn,
                    minWidth: 34,
                    px: 0,
                    flex: { xs: 1, md: "0 0 auto" },
                  }}
                  onClick={() => setExcelModalOpen(true)}
                >
                  <DownloadIcon sx={{ fontSize: 15 }} />
                </Button>
              </Tooltip>
            </Stack>
          </Grid>

          {/* Count + Add */}
          <Grid item xs={12} sm={12} md="auto" sx={{ ml: { md: "auto" } }}>
            <Stack direction="row" spacing={1.25} sx={{ width: "100%" }}>
              <Badge
                badgeContent={expiringCount}
                sx={{
                  flex: { xs: 1, md: "0 0 auto" },
                  "& .MuiBadge-badge": {
                    bgcolor: "#dc2626",
                    color: "#fff",
                    fontSize: "0.58rem",
                    fontWeight: 800,
                    minWidth: 16,
                    height: 16,
                    borderRadius: "8px",
                  },
                }}
              >
                <Button
                  onClick={() => setExpiringOpen(true)}
                  startIcon={<WarningIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    ...sq,
                    height: 34,
                    width: { xs: "100%", md: "auto" },
                    border: `1.5px solid #fca5a5`,
                    bgcolor: "#fee2e2",
                    color: "#dc2626",
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    textTransform: "none",
                    boxShadow: "none",
                    whiteSpace: "nowrap",
                    px: 1.5,
                    "&:hover": {
                      bgcolor: "#fecaca",
                      borderColor: "#dc2626",
                      boxShadow: "none",
                    },
                  }}
                >
                  Expiring LR
                </Button>
              </Badge>

              {/* Add LR */}
              <Button
                onClick={() => navigate("/dashboard/freight-form")}
                startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                sx={{
                  ...primaryBtn,
                  height: 34,
                  whiteSpace: "nowrap",
                  px: 2,
                  flex: { xs: 1, md: "0 0 auto" },
                  width: { xs: "100%", md: "auto" },
                }}
              >
                Add LR
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {dateFilter && (
        <Box sx={{ mb: 1.5, display: "flex", alignItems: "center" }}>
          <Chip
            label={
              dateFilter === "today"
                ? "📅 Showing: Today's LR"
                : "📅 Showing: This Month's LR"
            }
            onDelete={() => navigate("/dashboard/freight-list")}
            sx={{
              ...sq,
              bgcolor: "#eef2ff",
              color: T.primary,
              border: `1.5px solid ${T.border}`,
              fontWeight: 700,
              fontSize: "0.72rem",
              height: 30,
              "& .MuiChip-deleteIcon": { color: T.primary },
            }}
          />
        </Box>
      )}

      {/* ── Mobile Filter Button ── */}
      {isMobile && (
        <Button
          fullWidth
          size="small"
          onClick={() => setMobileFilterOpen(true)}
          startIcon={<FilterListIcon sx={{ fontSize: 14 }} />}
          sx={{ ...ghostBtn, height: 34, mb: 1 }}
        >
          Filters {hasActiveFilters ? `(Active)` : ""}
        </Button>
      )}

      {/* ── Mobile Filter Drawer ── */}
      <Drawer
        anchor="bottom"
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        PaperProps={{
          sx: {
            ...sq,
            maxHeight: "80vh",
            p: 2,
            borderTop: `2px solid ${T.primary}`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.5,
          }}
        >
          <Typography
            sx={{ fontWeight: 700, fontSize: "0.85rem", color: T.primary }}
          >
            Filter Options
          </Typography>
          <IconButton
            size="small"
            onClick={() => setMobileFilterOpen(false)}
            sx={{ ...sq, color: T.textSub }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
        <Stack spacing={1}>
          <Autocomplete
            size="small"
            options={partyOptions}
            getOptionLabel={(o) => o?.name || ""}
            value={partyOptions.find((o) => o.name === partyFilter) || null}
            onChange={(_, v) => setPartyFilter(v?.name || "")}
            renderInput={(p) => (
              <TextField
                {...p}
                placeholder="Party…"
                size="small"
                sx={filterInputSx}
              />
            )}
          />
          <TextField
            size="small"
            placeholder="Truck…"
            fullWidth
            value={truckFilter}
            onChange={(e) => setTruckFilter(e.target.value)}
            sx={filterInputSx}
          />
          <TextField
            type="date"
            size="small"
            fullWidth
            value={
              loadingDateFilter
                ? loadingDateFilter.toISOString().split("T")[0]
                : ""
            }
            onChange={(e) =>
              setLoadingDateFilter(
                e.target.value ? new Date(e.target.value) : null,
              )
            }
            sx={filterInputSx}
          />
          <TextField
            size="small"
            placeholder="Route (from-to)…"
            fullWidth
            value={fromFilter}
            onChange={(e) => setFromFilter(e.target.value)}
            sx={filterInputSx}
          />
          {hasActiveFilters && (
            <Button
              size="small"
              onClick={clearFilters}
              startIcon={<ClearIcon sx={{ fontSize: 13 }} />}
              sx={{ ...dangerBtn, height: 32 }}
            >
              Clear All
            </Button>
          )}
        </Stack>
        <Button
          fullWidth
          onClick={() => setMobileFilterOpen(false)}
          sx={{ ...primaryBtn, mt: 2, height: 36 }}
        >
          Apply Filters
        </Button>
      </Drawer>

      {/* ── Table / Mobile ── */}
      {isMobile ? (
        loading && data.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 10,
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: T.primary }} size={32} />
            <Typography sx={{ fontSize: "0.74rem", color: T.textSub }}>
              Loading…
            </Typography>
          </Box>
        ) : filteredData.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              border: `1.5px dashed ${T.border}`,
              bgcolor: T.white,
              ...sq,
            }}
          >
            <TruckIcon sx={{ fontSize: 44, color: T.border, mb: 1 }} />
            <Typography
              sx={{
                fontWeight: 700,
                color: T.text,
                mb: 0.5,
                fontSize: "0.9rem",
              }}
            >
              {dateFilter === "today"
                ? "No LR created today"
                : dateFilter === "month"
                  ? "No LR created this month"
                  : "No freight records found"}
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: T.textSub, mb: 2 }}>
              {dateFilter
                ? "Try viewing all LRs instead"
                : "Try adjusting your filters"}
            </Typography>
            {dateFilter && (
              <Button
                size="small"
                onClick={() => navigate("/dashboard/freight-list")}
                sx={{ ...ghostBtn, height: 32, px: 2 }}
              >
                View All LRs
              </Button>
            )}
          </Box>
        ) : (
          <Box
            id="freight-list-container-mobile"
            sx={{ maxHeight: "calc(100vh - 220px)", overflow: "auto" }}
          >
            <InfiniteScroll
              dataLength={data.length}
              next={() => fetchMoreParties(false)}
              hasMore={hasMore}
              loader={
                <Box sx={{ textAlign: "center", py: 1 }}>
                  <CircularProgress size={20} sx={{ color: T.primary }} />
                </Box>
              }
              scrollableTarget="freight-list-container-mobile"
            >
              {filteredData.map((row, idx) => (
                <MobileFreightCard
                  onPreview={handlePreviewOpen}
                  key={row.freight_id}
                  row={row}
                  idx={idx}
                  expanded={expandedCardId === row.freight_id}
                  onToggle={() => handleCardToggle(row.freight_id)}
                  onEdit={handleEditOpen}
                  onDelete={(e, id) => {
                    setDeleteAnchorEl(e.currentTarget);
                    setDeleteId(id);
                  }}
                  onPrint={handlePrintSingleRow}
                  onSettle={(e, id) => {
                    setSettleAnchorEl(e.currentTarget);
                    setSettleId(id);
                  }}
                />
              ))}
            </InfiniteScroll>

            {/* Mobile Summary */}
            <Paper
              elevation={0}
              sx={{
                ...sq,
                border: `1.5px solid ${T.border}`,
                bgcolor: T.primaryLight,
                p: 1.5,
                mt: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  color: T.primary,
                  mb: 0.75,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                📊 Summary
              </Typography>
              <Grid container spacing={0.75}>
                {[
                  ["Total LR", formatCurrency(totals.freight_amt), T.text],
                  [
                    "Net Payable",
                    formatCurrency(totals.net_payable),
                    T.primary,
                  ],
                  ["Paid", formatCurrency(totals.paid_amount), T.success],
                  ["Balance", formatCurrency(totals.bal_amount), T.red],
                ].map(([l, v, c]) => (
                  <Grid item xs={6} key={l}>
                    <Typography
                      sx={{
                        fontSize: "0.58rem",
                        color: T.textMuted,
                        fontWeight: 600,
                        textTransform: "uppercase",
                      }}
                    >
                      {l}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.75rem", fontWeight: 700, color: c }}
                    >
                      {v}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Box>
        )
      ) : loading && data.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            ...sq,
            border: `1.5px solid ${T.border}`,
            flex: 1,
            minHeight: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress sx={{ color: T.primary }} size={32} />
        </Paper>
      ) : filteredData.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            ...sq,
            border: `1.5px dashed ${T.border}`,
            bgcolor: T.white,
            textAlign: "center",
            py: 8,
            flex: 1,
          }}
        >
          <TruckIcon sx={{ fontSize: 44, color: T.border, mb: 1 }} />
          <Typography
            sx={{
              fontWeight: 700,
              color: T.text,
              mb: 0.5,
              fontSize: "0.95rem",
            }}
          >
            {dateFilter === "today"
              ? "No LR created today"
              : dateFilter === "month"
                ? "No LR created this month"
                : "No records found"}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: T.textSub, mb: 2 }}>
            {dateFilter
              ? "Try viewing all LRs instead"
              : "Try adjusting your filters"}
          </Typography>
          {dateFilter && (
            <Button
              size="small"
              onClick={() => navigate("/dashboard/freight-list")}
              sx={{ ...ghostBtn, height: 32, px: 2 }}
            >
              View All LRs
            </Button>
          )}
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{
            ...sq,
            border: `1.5px solid ${T.border}`,
            overflow: "hidden",
            boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <TableContainer
            id="freight-list-container"
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              position: "relative",
            }}
          >
            <InfiniteScroll
              dataLength={data.length}
              next={() => fetchMoreParties(false)}
              hasMore={hasMore}
              loader={
                <Box sx={{ textAlign: "center", py: 1 }}>
                  <CircularProgress size={20} sx={{ color: T.primary }} />
                </Box>
              }
              scrollableTarget="freight-list-container"
              style={{ overflow: "visible" }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 2800,
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  "& td, & th": {
                    py: 0,
                    px: 0.75,
                    fontSize: "0.75rem",
                    borderBottom: `1px solid #ccc`,
                    whiteSpace: "nowrap",
                    textAlign: "center",
                    lineHeight: 1.2,
                  },
                  "& th": {
                    fontWeight: 700,
                    color: T.headerText,
                    bgcolor: `${T.headerBg} !important`,
                    py: 0.2,
                    fontSize: "0.8rem",
                    letterSpacing: "0.01em",
                    borderBottom: `1px solid #ccc`,
                    lineHeight: 1.2,
                    height: 28,
                    position: "sticky",
                    top: 0,
                    zIndex: 15,
                  },
                  "& td": { height: 26 },
                }}
              >
                <TableHead>
                  <TableRow>
                    {[
                      { l: "Settle", sticky: true, left: 0, w: 40 },
                      { l: "#", sticky: true, left: 40, w: 35 },
                      { l: "LR No", sticky: true, left: 75, w: 80 },
                      { l: "Truck No", sticky: true, left: 155, w: 85 },
                      { l: "Party", w: 120 },
                      { l: "Broker", w: 120 }, // ← NEW
                      { l: "Loading Date", w: 85 },
                      { l: "Loading Out", w: 100 },
                      { l: "Unloading Date", w: 100 },
                      { l: "Unloading Out", w: 120 },
                      { l: "From", w: 90 },
                      { l: "To", w: 90 },
                      { l: "E-way Bill No", w: 100 },
                      { l: "E-way Bill Date", w: 100 },
                      { l: "E-way Validity", w: 130 },
                      { l: "Invoice No", w: 100 },
                      { l: "Classification", w: 130 },
                      { l: "Packing Method", w: 130 },
                      { l: "Truck Owner", w: 110 },
                      { l: "Phone", w: 95 },
                      { l: "Ref No", w: 90 },
                      { l: "Broker Total", w: 100 },
                      { l: "Broker Commission", w: 120 },
                      { l: "Broker Net", w: 100 },
                      { l: "Remark", w: 140 },
                      { l: "Additional Info", w: 160 },
                      { l: "Date", w: 90 },
                      { l: "Actions", sticky: true, right: 0, w: 85 },
                    ].map(({ l, sticky, left, right, w }) => (
                      <TableCell
                        key={l}
                        sx={{
                          minWidth: w,
                          bgcolor: `${T.headerBg} !important`,
                          color: `${T.headerText} !important`,
                          position: "sticky !important",
                          top: "0 !important",
                          zIndex: sticky ? "60 !important" : "20 !important",
                          ...(sticky && {
                            ...(left !== undefined && {
                              left: `${left}px !important`,
                            }),
                            ...(right !== undefined && {
                              right: `${right}px !important`,
                            }),
                          }),
                        }}
                      >
                        {l}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading && data.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={TABLE_COLUMN_COUNT}
                        sx={{ textAlign: "center", py: 6, border: "none" }}
                      >
                        <CircularProgress sx={{ color: T.primary }} size={32} />
                      </TableCell>
                    </TableRow>
                  ) : filteredData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={TABLE_COLUMN_COUNT}
                        sx={{ textAlign: "center", py: 6, border: "none" }}
                      >
                        <TruckIcon
                          sx={{
                            fontSize: 40,
                            color: T.border,
                            mb: 1,
                            display: "block",
                            mx: "auto",
                          }}
                        />
                        <Typography
                          sx={{
                            color: T.textSub,
                            fontSize: "0.8rem",
                            fontWeight: 600,
                          }}
                        >
                          {dateFilter === "today"
                            ? "No LR created today"
                            : dateFilter === "month"
                              ? "No LR created this month"
                              : "No records found"}
                        </Typography>
                        {dateFilter && (
                          <Button
                            size="small"
                            onClick={() => navigate("/dashboard/freight-list")}
                            sx={{ ...ghostBtn, height: 30, mt: 1.5, px: 2 }}
                          >
                            View All LRs
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredData.map((row, idx) => {
                      const isEven = idx % 2 === 0;
                      const rowBg = isEven ? T.baseRow : T.altRow;
                      const pkg = getPrimaryPackage(row);
                      const stickyCell = {
                        bgcolor: `${rowBg} !important`,
                        zIndex: "25 !important",
                        position: "sticky !important",
                      };

                      return (
                        <TableRow
                          key={row.freight_id}
                          sx={{
                            "& > td": {
                              bgcolor: rowBg,
                              transition: "background-color 0.12s",
                            },
                            "&:hover > td": {
                              bgcolor: `${T.rowHover} !important`,
                            },
                          }}
                        >
                          {/* Settle checkbox */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 0,
                              ...stickyCell,
                              p: "2px 4px",
                            }}
                          >
                            <Checkbox
                              size="small"
                              disabled={row.is_completed}
                              onClick={(e) => {
                                setSettleAnchorEl(e.currentTarget);
                                setSettleId(row.freight_id);
                              }}
                              sx={{
                                p: 0,
                                color: T.primary,
                                "&.Mui-checked": { color: T.primary },
                              }}
                            />
                          </TableCell>

                          {/* # */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 40,
                              ...stickyCell,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                color: T.textMuted,
                              }}
                            >
                              {idx + 1}
                            </Typography>
                          </TableCell>

                          {/* LR No — now sticky at 75 */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 75,
                              ...stickyCell,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                color: T.text,
                              }}
                            >
                              {row.lr_number || "—"}
                            </Typography>
                          </TableCell>

                          {/* Truck No — now sticky at 155 */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 155,
                              ...stickyCell,
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                color: T.primary,
                              }}
                            >
                              {row.truck_no}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <TruncatedCell
                              text={row.party_name}
                              maxWidth={120}
                            />
                          </TableCell>
                          <TableCell>
                            <TruncatedCell
                              text={row.broker_party_name}
                              maxWidth={120}
                            />
                          </TableCell>
                          <TableCell>{formatDate(row.loading_date)}</TableCell>
                          <TableCell>
                            {formatDate(row.loading_out_date)}
                          </TableCell>
                          <TableCell>
                            {formatDate(row.unloading_date)}
                          </TableCell>
                          <TableCell>
                            {formatDate(row.unloading_out_date)}
                          </TableCell>
                          <TableCell>
                            <TruncatedCell
                              text={row.from_location}
                              maxWidth={100}
                            />
                          </TableCell>
                          <TableCell>
                            <TruncatedCell
                              text={row.to_location}
                              maxWidth={100}
                            />
                          </TableCell>

                          <TableCell>
                            <TruncatedCell
                              text={row.eway_bill_no}
                              maxWidth={100}
                            />
                          </TableCell>
                          <TableCell>
                            {formatDate(row.eway_bill_date)}
                          </TableCell>
                          <TableCell>
                            <Typography
                              sx={{ fontSize: "0.72rem", color: T.text }}
                            >
                              {formatDate(row.eway_validity_date)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <TruncatedCell
                              text={row.invoice_no}
                              maxWidth={100}
                            />
                          </TableCell>
                          <TableCell>
                            <TruncatedCell
                              text={row.classification_of_goods}
                              maxWidth={100}
                            />
                          </TableCell>
                          <TableCell>
                            <TruncatedCell
                              text={row.method_of_packing}
                              maxWidth={100}
                            />
                          </TableCell>
                          <TableCell>
                            <TruncatedCell
                              text={row.truck_owner}
                              maxWidth={100}
                            />
                          </TableCell>
                          <TableCell>{row.phone_no || "—"}</TableCell>

                          <TableCell>
                            <TruncatedCell
                              text={getRefNo(row)}
                              maxWidth={100}
                            />
                          </TableCell>

                          <TableCell>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                color: T.text,
                              }}
                            >
                              {row.broker_total_amt
                                ? formatCurrency(row.broker_total_amt)
                                : "—"}
                            </Typography>
                          </TableCell>

                          {/* Broker Commission Amount — NEW */}
                          <TableCell>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                color: "#d97706",
                              }}
                            >
                              {row.broker_commission_amt
                                ? formatCurrency(row.broker_commission_amt)
                                : "—"}
                            </Typography>
                          </TableCell>

                          {/* Broker Net Amount — NEW */}
                          <TableCell>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 700,
                                color: T.primary,
                              }}
                            >
                              {row.broker_net_amt
                                ? formatCurrency(row.broker_net_amt)
                                : "—"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <TruncatedCell text={row.remark} maxWidth={140} />
                          </TableCell>
                          <TableCell>
                            <TruncatedCell
                              text={row.additional_info}
                              maxWidth={160}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={formatDate(row.created_at)}
                              size="small"
                              sx={{
                                fontSize: "0.6rem",
                                height: 16,
                                ...sq,
                                bgcolor: T.white,
                                color: T.primary,
                                border: `1px solid ${T.border}`,
                                fontWeight: 700,
                                "& .MuiChip-label": { px: 0.75 },
                              }}
                            />
                          </TableCell>
                          {/* Actions — sticky right */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              right: 0,
                              ...stickyCell,
                              py: "2px !important",
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="center"
                            >
                              <ActionButton
                                tip="Preview"
                                icon={<Visibility />}
                                isView
                                onClick={() => handlePreviewOpen(row)}
                              />
                              <ActionButton
                                tip="Edit"
                                icon={<EditIcon />}
                                isEdit
                                onClick={() => handleEditOpen(row)}
                              />
                              <ActionButton
                                tip="Delete"
                                icon={<DeleteIcon />}
                                isDelete
                                onClick={(e) => {
                                  setDeleteAnchorEl(e.currentTarget);
                                  setDeleteId(row.freight_id);
                                }}
                              />
                              <ActionButton
                                tip="Print"
                                icon={<PrintIcon />}
                                isPrint
                                onClick={() => handlePrintSingleRow(row)}
                              />
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </InfiniteScroll>
          </TableContainer>
        </Paper>
      )}

      {/* ── Edit Dialog ── */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...sq,
            border: `1.5px solid ${T.border}`,
            boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
            overflow: "hidden",
          },
        }}
      >
        <DialogHeader
          title="Edit LR Record"
          onClose={() => setEditDialogOpen(false)}
        />
        <DialogContent sx={{ p: 2.5, bgcolor: T.white }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <Stack direction="row" spacing={2}>
                {[
                  {
                    key: "is_remember",
                    label: "Remember",
                    handler: (e) =>
                      setEditRow((p) => ({
                        ...p,
                        is_remember: e.target.checked,
                      })),
                  },
                  {
                    key: "is_not_rec",
                    label: "LR Not Received",
                    handler: (e) =>
                      handleEditChange("is_not_rec", e.target.checked),
                  },
                ].map(({ key, label, handler }) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={editRow[key] || false}
                        onChange={handler}
                        sx={{
                          color: T.primary,
                          "&.Mui-checked": { color: T.primary },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: "0.75rem" }}>
                        {label}
                      </Typography>
                    }
                  />
                ))}
              </Stack>
            </Grid>
            {[
              { name: "party_name", label: "Party Name", type: "autocomplete" },
              { name: "lr_number", label: "LR No", disabled: true },
              { name: "from_location", label: "From Location" },
              { name: "to_location", label: "To Location" },
              { name: "loading_date", label: "Loading Date", type: "date" },
              { name: "unloading_date", label: "Unloading Date", type: "date" },
              { name: "truck_no", label: "Truck No" },
              { name: "truck_owner", label: "Truck Owner" },
              { name: "phone_no", label: "Phone No." },
              { name: "pan_no", label: "PAN No" },
              { name: "loading_wt", label: "Loading Wt", type: "number" },
              { name: "received_wt", label: "Receiving Wt", type: "number" },
              { name: "shortage_rate", label: "Shortage Rate", type: "number" },
              { name: "freight_rate", label: "LR Rate", type: "number" },
              { name: "tds_per", label: "TDS (%)", type: "number" },
              { name: "ad_mines_exp", label: "Ad Mines", type: "number" },
              { name: "ad_diesel_exp", label: "Ad Diesel", type: "number" },
              { name: "ad_cash_exp", label: "Ad Cash", type: "number" },
              { name: "commission", label: "Commission", type: "number" },
              { name: "other_exp", label: "Others Exp.", type: "number" },
              {
                name: "demurrage_days",
                label: "Demurrage Days",
                type: "number",
              },
              {
                name: "demurrage_rate",
                label: "Demurrage Rate",
                type: "number",
              },
            ].map(({ name, label, type, disabled }) => (
              <Grid item xs={6} md={3} key={name}>
                {type === "autocomplete" ? (
                  <Autocomplete
                    options={partyOptions}
                    getOptionLabel={(o) => o?.name || ""}
                    value={
                      partyOptions.find((o) => o.name === editRow.party_name) ||
                      null
                    }
                    onChange={(_, v) =>
                      handleEditChange("party_id", v?.party_id || "")
                    }
                    renderInput={(p) => (
                      <TextField
                        {...p}
                        label={label}
                        size="small"
                        sx={inputSx}
                      />
                    )}
                  />
                ) : (
                  <TextField
                    label={label}
                    fullWidth
                    size="small"
                    type={type || "text"}
                    value={editRow[name] || ""}
                    onChange={(e) => handleEditChange(name, e.target.value)}
                    InputProps={disabled ? { readOnly: true } : {}}
                    InputLabelProps={type === "date" ? { shrink: true } : {}}
                    sx={disabled ? disabledFieldSx : inputSx}
                  />
                )}
              </Grid>
            ))}
            {[
              { name: "remark", label: "Remark" },
              { name: "additional_info", label: "Additional Info" },
            ].map(({ name, label }) => (
              <Grid item xs={12} key={name}>
                <TextField
                  label={label}
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  value={editRow[name] || ""}
                  onChange={(e) => handleEditChange(name, e.target.value)}
                  sx={inputSx}
                />
              </Grid>
            ))}
            <Grid item xs={6}>
              <TextField
                label="Receive Amount"
                fullWidth
                type="number"
                size="small"
                value={editRow.receive_amount || 0}
                onChange={(e) =>
                  handleEditChange("receive_amount", e.target.value)
                }
                disabled={!editRow.is_bill}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Difference Amount"
                fullWidth
                size="small"
                value={(
                  (editRow.net_amount || 0) - (editRow.receive_amount || 0)
                ).toFixed(2)}
                disabled
                sx={disabledFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Remark for Payment"
                fullWidth
                size="small"
                value={editRow.payment_remark || ""}
                onChange={(e) =>
                  handleEditChange("payment_remark", e.target.value)
                }
                disabled={!editRow.is_bill}
                sx={inputSx}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            gap: 1,
            bgcolor: T.white,
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <Button
            size="small"
            onClick={() => setEditDialogOpen(false)}
            sx={{ ...ghostBtn, height: 32, px: 2 }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            onClick={handleEditSave}
            sx={{ ...primaryBtn, height: 32, px: 2.5 }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Export Dialog ── */}
      <Dialog
        open={excelModalOpen}
        onClose={() => setExcelModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            ...sq,
            border: `1.5px solid ${T.border}`,
            boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
            overflow: "hidden",
          },
        }}
      >
        <DialogHeader
          title="Select Rows for Excel Export"
          onClose={() => setExcelModalOpen(false)}
        />
        <DialogContent sx={{ p: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by party name…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessIcon sx={{ fontSize: 14, color: T.primary }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1.5, ...filterInputSx }}
          />
          <TableContainer sx={{ maxHeight: "50vh", overflow: "auto" }}>
            <Table
              stickyHeader
              size="small"
              sx={{
                "& td, & th": {
                  fontSize: "0.75rem",
                  borderBottom: `1px solid #ccc`,
                  lineHeight: 1.2,
                  height: 26,
                },
                "& th": {
                  fontWeight: 700,
                  color: T.headerText,
                  bgcolor: `${T.headerBg} !important`,
                  fontSize: "0.8rem",
                  letterSpacing: "0.01em",
                  borderBottom: `1px solid #ccc`,
                  height: 28,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  {[
                    "",
                    "ID",
                    "Truck No",
                    "Party Name",
                    "LR No",
                    "Loading Date",
                    "Net Payable",
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        bgcolor: `${T.headerBg} !important`,
                        color: `${T.headerText} !important`,
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData
                  .filter((r) =>
                    r.party_name
                      ?.toLowerCase()
                      .includes(searchQuery.toLowerCase()),
                  )
                  .map((row, idx) => {
                    const isEven = idx % 2 === 0;
                    const rowBg = isEven ? T.baseRow : T.altRow;
                    return (
                      <TableRow
                        key={row.freight_id}
                        sx={{
                          "& > td": { bgcolor: rowBg },
                          "&:hover > td": {
                            bgcolor: `${T.rowHover} !important`,
                          },
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={selectedRows.includes(row.freight_id)}
                            onChange={(e) =>
                              setSelectedRows((p) =>
                                e.target.checked
                                  ? [...p, row.freight_id]
                                  : p.filter((id) => id !== row.freight_id),
                              )
                            }
                            sx={{
                              color: T.primary,
                              "&.Mui-checked": { color: T.primary },
                            }}
                          />
                        </TableCell>
                        <TableCell>{row.freight_id}</TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: T.primary,
                              fontSize: "0.72rem",
                            }}
                          >
                            {row.truck_no}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.party_name}</TableCell>
                        <TableCell>{row.lr_number}</TableCell>
                        <TableCell>{formatDate(row.loading_date)}</TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: T.primary,
                              fontSize: "0.72rem",
                            }}
                          >
                            ₹{row.net_payable}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            gap: 1,
            bgcolor: T.white,
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <Button
            size="small"
            onClick={() => setExcelModalOpen(false)}
            sx={{ ...ghostBtn, height: 32, px: 2 }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            onClick={handleExportSelectedToExcel}
            sx={{ ...primaryBtn, height: 32, px: 2.5 }}
          >
            Export ({selectedRows.length})
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={previewOpen}
        onClose={handlePreviewClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            ...sq,
            height: "92vh",
            border: `1.5px solid ${T.border}`,
            overflow: "hidden",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.2)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        <DialogHeader title="LR Receipt Preview" onClose={handlePreviewClose} />
        <DialogContent
          sx={{ p: 0, flex: 1, bgcolor: "#fff", overflow: "hidden" }}
        >
          {previewLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <CircularProgress sx={{ color: "#4f46e5" }} />
            </Box>
          ) : previewUrl ? (
            <iframe
              title="freight-receipt-preview"
              src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH&zoom=page-width`}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                display: "block",
                backgroundColor: "#fff",
              }}
            />
          ) : null}
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            gap: 1,
            bgcolor: T.white,
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <Button
            size="small"
            onClick={handlePreviewClose}
            sx={{ ...ghostBtn, height: 32, px: 2 }}
          >
            Close
          </Button>
          <Button
            size="small"
            disabled={!previewUrl}
            onClick={() => window.open(previewUrl, "_blank")}
            startIcon={<PrintIcon sx={{ fontSize: 14 }} />}
            sx={{ ...primaryBtn, height: 32, px: 2.5 }}
          >
            Open Full / Print
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Expiring LR Dialog ── */}
      <ExpiringLRDialog
        open={expiringOpen}
        onClose={() => setExpiringOpen(false)}
        data={data}
        onEdit={handleEditOpen}
        onPreview={handlePreviewOpen}
      />

      {/* ── Popovers ── */}
      <DeletePopover
        anchorEl={deleteAnchorEl}
        onClose={() => {
          setDeleteAnchorEl(null);
          setDeleteId(null);
        }}
        onConfirm={() => handleDelete(deleteId)}
      />
      <SettlePopover
        anchorEl={settleAnchorEl}
        onClose={() => {
          setSettleAnchorEl(null);
          setSettleId(null);
        }}
        onConfirm={() => handleComplete(settleId)}
      />
    </Box>
  );
}
